import { orientationsFromConfig, orientationSummary, systemDesignForOrientation, updateOrientation } from "./orientations";
import { physicalLayoutCount, physicalLayoutDesignKey } from "../../utils/physicalLayoutDesign";
import { modulePositions } from "../../utils/moduleShading";

// Apply wiring to the exact edited ground rows, never rebuild their geometry.
// The caller validates the complete electrical design before calling this and
// commits the returned config only after mapping succeeds.
export async function applyGroundPlanDesign(config, plan, fetchMapping, greek = false) {
  const fail = () => { throw new Error(greek
    ? "Τα νέα strings δεν χωρούν στις σειρές με την επιλεγμένη διαδρομή. Ελέγξτε τη διαδρομή strings ή τα μήκη σειρών· δεν εφαρμόστηκε αλλαγή."
    : "The new string mapping could not be completed with the selected row routing. Check string routing or row lengths; no change was applied."); };
  const planes = orientationsFromConfig(config);
  const plane = planes.find(p => p.id === plan.id);
  const count = physicalLayoutCount(plan.spacing);
  if (!plane || !(count > 0) || orientationSummary(config).find(p => p.id === plan.id)?.modules !== count) fail();
  const scoped = systemDesignForOrientation(config.systemDesign, planes, plan.id);
  const spacing = { ...plan.spacing, string_overrides: null };
  let mapping;
  try {
    mapping = await fetchMapping({ systemDesign: scoped, rowModules: spacing.row_modules,
      tiers: Number(spacing.row_vertical_modules) || 1,
      allowCrossRows: (spacing.string_routing || "continuous") === "continuous",
      excludedSlots: spacing.excluded_slots?.length ? spacing.excluded_slots : undefined });
  } catch { fail(); }
  const positions = modulePositions(mapping, Number(spacing.row_vertical_modules) || 1);
  const slots = new Set(positions.map(p => `${p.row}:${p.column}:${p.tier}`));
  const outside = positions.some(p => !Number.isInteger(p.row) || p.row < 1 || p.row > spacing.row_modules.length ||
    !Number.isInteger(p.column) || p.column < 0 || p.column >= spacing.row_modules[p.row - 1] ||
    !Number.isInteger(p.tier) || p.tier < 0 || p.tier >= (Number(spacing.row_vertical_modules) || 1) ||
    spacing.excluded_slots?.includes(`${p.row}:${p.column}:${p.tier}`));
  if (!mapping?.check?.consistent || positions.length !== count || slots.size !== count || outside) fail();
  spacing.module_positions = positions;
  spacing.module_assignments = spacing.manual_layout_edited ? positions : null;
  spacing.module_mapping_valid = true;
  spacing.scene_review_required = true;
  spacing.electrical_design_key = physicalLayoutDesignKey(scoped);
  spacing.fit_to_modules = count;
  delete spacing.scene_views;
  let next = plane.primary ? { ...config, pv_design: { ...config.pv_design, spacingInfo: spacing } }
    : updateOrientation(config, plane.id, { spacingInfo: spacing });
  // A resized row can change shadows on neighbouring surfaces too.
  for (const p of planes) {
    const record = p.primary ? next.pv_design : p;
    const patch = { sceneViews: [], obstacleShading: { ...record?.obstacleShading, stale: true } };
    next = p.primary ? { ...next, pv_design: { ...next.pv_design, ...patch } }
      : updateOrientation(next, p.id, patch);
  }
  return { config: next, changedIds: [plan.id], pendingIds: [] };
}
